import React, { useEffect, useState } from "react";
import { 
  Heart, 
  ShoppingBag, 
  Stethoscope, 
  Briefcase, 
  Sprout, 
  HeartHandshake, 
  Wrench, 
  Smile, 
  Check, 
  Phone, 
  Mail, 
  MapPin, 
  Trash2, 
  Lock, 
  Unlock, 
  Search, 
  Menu, 
  X, 
  Scale, 
  ShieldAlert, 
  ChevronRight, 
  Coffee, 
  Sparkles, 
  ThumbsUp 
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { HOME } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Reusable custom heart element for floating animations
const FloatingHeart = ({ x, y, id, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete(id);
    }, 1200);
    return () => clearTimeout(timer);
  }, [id, onComplete]);

  return (
    <span 
      className="absolute text-red-500 text-lg pointer-events-none z-50 animate-float-heart"
      style={{ left: x, top: y }}
    >
      ❤️
    </span>
  );
};

export default function App() {
  // Navigation states
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Form states
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formService, setFormService] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [formPrivacy, setFormPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // Admin Dashboard states
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [adminPin, setAdminPin] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgSearch, setMsgSearch] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);

  // Legal Modals state
  const [activeModal, setActiveModal] = useState(null); // 'impressum' or 'datenschutz' or null

  // Floating hearts state
  const [floatingHearts, setFloatingHearts] = useState([]);

  // Interactive Slogan Quotes (from the flyer, designed beautifully)
  const hildeQuotes = [
    {
      text: "Ich kann zwar nicht alles... aber was ich kann, mach ich gern! ❤️",
      tag: "Unser HerzWerk Versprechen"
    },
    {
      text: "Ein Anruf genügt – wir kümmern uns! Keine Sorge, wir lassen Sie nicht im Regen stehen.",
      tag: "Zuverlässige Hilfe"
    },
    {
      text: "Mit Herz, Verstand und einem Lächeln für Sie da! Denn geteilte Freude ist doppelte Freude.",
      tag: "Unser Tagesmotto"
    },
    {
      text: "Zuhause ist es doch am schönsten! Und genau dort helfen wir Ihnen, so lange wie möglich zu bleiben.",
      tag: "HerzWerk Philosophie"
    }
  ];
  const [currentQuoteIdx, setCurrentQuoteIdx] = useState(0);
  const [quoteAnimating, setQuoteAnimating] = useState(false);

  const handleNextQuote = () => {
    if (quoteAnimating) return;
    setQuoteAnimating(true);
    setTimeout(() => {
      setCurrentQuoteIdx((prev) => (prev + 1) % hildeQuotes.length);
      setQuoteAnimating(false);
    }, 300);
  };

  // Add floating heart on click
  const triggerHeartClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - 10;
    const y = e.clientY - rect.top - 10;
    const newHeart = {
      x,
      y,
      id: Date.now() + Math.random()
    };
    setFloatingHearts((prev) => [...prev, newHeart]);
  };

  const removeHeart = (id) => {
    setFloatingHearts((prev) => prev.filter((h) => h.id !== id));
  };

  // Submit contact form
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(null);
    setSubmitError(null);

    if (!formName || !formEmail || !formPhone || !formService || !formMessage) {
      setSubmitError("Bitte füllen Sie alle Felder aus.");
      setIsSubmitting(false);
      return;
    }

    if (!formPrivacy) {
      setSubmitError("Bitte stimmen Sie der Datenschutzerklärung zu, um fortzufahren.");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/contact`, {
        name: formName,
        email: formEmail,
        phone: formPhone,
        service: formService,
        message: formMessage,
      });

      if (response.data.success) {
        setSubmitSuccess(response.data.message);
        // Reset form
        setFormName("");
        setFormEmail("");
        setFormPhone("");
        setFormService("");
        setFormMessage("");
        setFormPrivacy(false);
      } else {
        setSubmitError("Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.");
      }
    } catch (err) {
      console.error("Error submitting contact form:", err);
      setSubmitError(
        err.response?.data?.detail || 
        "Verbindung zum Server fehlgeschlagen. Bitte überprüfen Sie Ihre Internetverbindung."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch messages for admin
  const fetchMessages = async () => {
    setMsgLoading(true);
    try {
      const response = await axios.get(`${API}/contact`);
      const mappedMessages = response.data.map(msg => ({
        ...msg,
        id: msg.id || msg._id
      }));
      setMessages(mappedMessages);
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      setMsgLoading(false);
    }
  };

  // Authenticate admin
  const handleAdminAuth = (e) => {
    e.preventDefault();
    if (adminPin === "1234") {
      setIsAuthenticated(true);
      fetchMessages();
    } else {
      alert("Falsche PIN. Bitte versuchen Sie es erneut.");
    }
  };

  // Delete message
  const handleDeleteMessage = async (id) => {
    if (window.confirm("Möchten Sie diese Nachricht wirklich löschen?")) {
      try {
        const response = await axios.delete(`${API}/contact/${id}`);
        if (response.data.success) {
          setMessages(messages.filter((msg) => msg.id !== id));
        }
      } catch (err) {
        console.error("Error deleting message:", err);
        alert("Fehler beim Löschen der Nachricht.");
      }
    }
  };

  // Log out admin
  const handleAdminLogout = () => {
    setIsAuthenticated(false);
    setAdminPin("");
    setMessages([]);
  };

  // Filtered messages
  const filteredMessages = messages.filter((msg) => {
    const q = msgSearch.toLowerCase();
    return (
      msg.name.toLowerCase().includes(q) ||
      msg.email.toLowerCase().includes(q) ||
      msg.phone.toLowerCase().includes(q) ||
      msg.service.toLowerCase().includes(q) ||
      msg.message.toLowerCase().includes(q)
    );
  });

  // Services info matching flyer with beautiful icons and styling
  const services = [
    {
      title: "Einkaufsservice",
      description: "Wir übernehmen Ihre Einkäufe oder begleiten Sie gerne persönlich. Ganz nach Ihrem Wunsch.",
      icon: ShoppingBag,
      colSpan: "md:col-span-4",
      bg: "bg-[#FDFBF7]",
      border: "border-[#E8E2D5]"
    },
    {
      title: "Arztbegleitung & Fahrdienst",
      description: "Wir begleiten Sie zum Arzt, zur Apotheke oder zu anderen Terminen - sicher, pünktlich und mit Geduld.",
      icon: Stethoscope,
      colSpan: "md:col-span-4",
      bg: "bg-[#DDEBE6]",
      border: "border-[#B2C7C0]"
    },
    {
      title: "Reisebegleitung",
      description: "Ob Bahn, Bus oder Flug – wir sind an Ihrer Seite und sorgen für eine entspannte Reise.",
      icon: Briefcase,
      colSpan: "md:col-span-3",
      bg: "bg-[#F4EFE6]",
      border: "border-[#E8E2D5]"
    },
    {
      title: "Gartenarbeiten",
      description: "Rasen mähen, Blumen pflanzen, Unkraut jäten – wir bringen Ihren Garten wieder zum Blühen.",
      icon: Sprout,
      colSpan: "md:col-span-5",
      bg: "bg-[#FDFBF7]",
      border: "border-[#E8E2D5]"
    },
    {
      title: "Tierbetreuung",
      description: "Wir kümmern uns liebevoll um Ihre Tiere, wenn Sie Hilfe brauchen – Füttern, Gassi gehen, Streicheleinheiten inklusive.",
      icon: HeartHandshake,
      colSpan: "md:col-span-5",
      bg: "bg-[#DDEBE6]",
      border: "border-[#B2C7C0]"
    },
    {
      title: "Handwerkliche Arbeiten",
      description: "Kleine Reparaturen, Regale aufhängen, Leuchtmittel wechseln, Möbel aufbauen und vieles mehr.",
      icon: Wrench,
      colSpan: "md:col-span-3",
      bg: "bg-[#F4EFE6]",
      border: "border-[#E8E2D5]"
    },
    {
      title: "Weitere Unterstützung",
      description: "Hilfe im Haushalt, Unterstützung bei Formularen, Begleitung bei Spaziergängen, Gesellschaft leisten und einfach da sein.",
      icon: Smile,
      colSpan: "md:col-span-8",
      bg: "bg-[#FDFBF7]",
      border: "border-[#E8E2D5]"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1C3B2B] relative selection:bg-[#B34B62] selection:text-white">
      {/* 1. Header & Navigation */}
      <header className="sticky top-0 z-40 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#E8E2D5] transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-24 flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-3.5 group" data-testid={HOME.logo}>
            {/* Custom SVG Flyer-like Logo: House with heart in roof */}
            <div className="w-12 h-12 bg-[#B34B62] rounded-2xl flex items-center justify-center text-white transform group-hover:scale-105 group-hover:rotate-3 transition-all shadow-md relative overflow-hidden">
              <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white stroke-none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 12h3v8h14v-8h3L12 2zm0 6c1.1 0 2 .9 2 2 0 1.1-.9 2-2 2s-2-.9-2-2c0-1.1.9-2 2-2zm0 8.5c-2.5 0-4.5-1.5-4.5-3.5 0-.5.5-1 1-1s1 .5 1 1c0 1 1.1 1.5 2.5 1.5s2.5-.5 2.5-1.5c0-.5.5-1 1-1s1 .5 1 1c0 2-2 3.5-4.5 3.5z" />
                <path d="M12 12c.5 0 .9-.2 1.2-.5.3-.3.5-.7.5-1.2h-3.4c0 .5.2.9.5 1.2.3.3.7.5 1.2.5z" opacity="0.3" />
              </svg>
              <Heart className="w-3.5 h-3.5 fill-[#B34B62] text-[#B34B62] absolute top-3.5 left-4.2" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-3xl font-extrabold tracking-tight text-[#1C3B2B] font-serif leading-none">HerzWerk</span>
                <Heart className="w-4 h-4 text-[#B34B62] fill-[#B34B62]" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#3A5A4A] font-extrabold block mt-1.5">Alltagshilfe & Hausservice</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <a href="#services" className="text-sm font-semibold hover:text-[#B34B62] transition-colors relative group">
              Dienstleistungen
              <span className="absolute bottom-[-6px] left-0 w-0 h-[2px] bg-[#B34B62] group-hover:w-full transition-all duration-300" />
            </a>
            <a href="#warum-uns" className="text-sm font-semibold hover:text-[#B34B62] transition-colors relative group">
              Warum wir?
              <span className="absolute bottom-[-6px] left-0 w-0 h-[2px] bg-[#B34B62] group-hover:w-full transition-all duration-300" />
            </a>
            <a href="#kontakt" className="text-sm font-semibold hover:text-[#B34B62] transition-colors relative group">
              Kontakt
              <span className="absolute bottom-[-6px] left-0 w-0 h-[2px] bg-[#B34B62] group-hover:w-full transition-all duration-300" />
            </a>
            <a 
              data-testid={HOME.emergentLink} 
              href="https://emergent.sh" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs uppercase tracking-[0.15em] text-[#B34B62] hover:text-[#9A3A4D] font-extrabold border border-[#B34B62]/20 px-3 py-1 rounded-full bg-[#B34B62]/5"
            >
              Emergent
            </a>
          </nav>

          <div className="hidden lg:block relative">
            <a 
              href="#kontakt" 
              onClick={triggerHeartClick}
              className="bg-[#B34B62] text-white hover:bg-[#9A3A4D] px-7 py-3.5 rounded-full text-sm font-bold shadow-md hover:shadow-lg transition-all inline-flex items-center gap-2 transform hover:-translate-y-0.5 active:translate-y-0 relative overflow-hidden"
            >
              <Phone className="w-4.5 h-4.5 stroke-[2.5]" /> Jetzt anrufen / Anfragen
              {floatingHearts.map((heart) => (
                <FloatingHeart 
                  key={heart.id} 
                  x={heart.x} 
                  y={heart.y} 
                  id={heart.id} 
                  onComplete={removeHeart} 
                />
              ))}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            className="lg:hidden p-2.5 text-[#1C3B2B] hover:text-[#B34B62] rounded-xl hover:bg-[#F4EFE6] transition-colors"
            aria-label="Toggle Menu"
          >
            {mobileMenuOpen ? <X className="w-6.5 h-6.5" /> : <Menu className="w-6.5 h-6.5" />}
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="lg:hidden bg-[#FDFBF7] border-t border-[#E8E2D5] absolute top-24 left-0 w-full overflow-hidden shadow-2xl z-40"
            >
              <div className="px-6 py-8 flex flex-col gap-5">
                <a 
                  href="#services" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-lg font-bold text-[#1C3B2B] hover:text-[#B34B62] flex items-center justify-between py-2 border-b border-[#E8E2D5]/60"
                >
                  Dienstleistungen <ChevronRight className="w-5 h-5 text-[#B34B62]" />
                </a>
                <a 
                  href="#warum-uns" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-lg font-bold text-[#1C3B2B] hover:text-[#B34B62] flex items-center justify-between py-2 border-b border-[#E8E2D5]/60"
                >
                  Warum wir? <ChevronRight className="w-5 h-5 text-[#B34B62]" />
                </a>
                <a 
                  href="#kontakt" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-lg font-bold text-[#1C3B2B] hover:text-[#B34B62] flex items-center justify-between py-2"
                >
                  Kontakt <ChevronRight className="w-5 h-5 text-[#B34B62]" />
                </a>
                <a 
                  data-testid={HOME.emergentLink} 
                  href="https://emergent.sh" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm font-extrabold text-[#B34B62] uppercase tracking-widest bg-[#B34B62]/5 border border-[#B34B62]/10 py-3 rounded-2xl text-center"
                >
                  Emergent
                </a>
                <a 
                  href="tel:01702657017"
                  className="bg-[#B34B62] text-white text-center py-4.5 rounded-full font-extrabold inline-flex justify-center items-center gap-2 shadow-lg"
                >
                  <Phone className="w-5 h-5" /> 0170 / 265 70 17
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* 2. Hero Section */}
      <section id="hero" className="relative pt-12 pb-24 md:pt-20 md:pb-36 overflow-hidden bg-gradient-to-b from-[#FDFBF7] via-[#FDFBF7] to-[#F4EFE6]">
        {/* Background Decorative Art Elements resembling the flyer */}
        <div className="absolute top-20 right-[-10%] w-[600px] h-[600px] rounded-full bg-[#DDEBE6]/50 blur-3xl -z-10" />
        <div className="absolute bottom-0 left-[-5%] w-[450px] h-[450px] rounded-full bg-[#B34B62]/5 blur-3xl -z-10" />
        
        {/* Cute Floating Hearts in Background */}
        <div className="absolute top-1/4 left-1/3 text-[#B34B62]/20 text-3xl rotate-12 -z-10 font-sans pointer-events-none select-none">❤️</div>
        <div className="absolute bottom-1/4 right-1/4 text-[#B34B62]/10 text-4xl -rotate-12 -z-10 font-sans pointer-events-none select-none">❤️</div>
        <div className="absolute top-2/3 left-10 text-[#B34B62]/15 text-2xl rotate-45 -z-10 font-sans pointer-events-none select-none">❤️</div>

        <div className="max-w-7xl mx-auto px-6 sm:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Hero Text */}
          <div className="lg:col-span-7 space-y-8 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#DDEBE6] border border-[#B2C7C0] text-[#1C3B2B] text-xs font-bold uppercase tracking-widest shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-[#B34B62] fill-[#B34B62] animate-spin" style={{ animationDuration: '6s' }} /> 
              Liebevolle Alltagshilfe für Senioren in Bayern
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight font-medium font-serif text-[#1C3B2B]">
              Wir packen an, damit Sie Ihr Leben <span className="text-[#B34B62] font-cursive italic text-5xl sm:text-6xl block sm:inline mt-1 sm:mt-0">genießen</span> können!
            </h1>
            
            {/* Elegant, warm handwritten quote from the flyer */}
            <p className="font-handwritten text-2xl sm:text-3xl text-[#B34B62] font-bold tracking-wide">
              &ldquo;Ihre Unterstützung im Alltag – zuverlässig, herzlich & persönlich&rdquo; ❤️
            </p>

            <p className="text-base sm:text-lg text-[#3A5A4A] max-w-xl mx-auto lg:mx-0 leading-relaxed font-medium">
              Zuhause ist es doch am schönsten! HerzWerk sorgt dafür, dass Sie oder Ihre Angehörigen den Alltag entspannt, sicher und voller Lebensfreude bewältigen – mit einer helfenden Hand und einem offenen Ohr.
            </p>

            <div className="bg-white p-5 rounded-[2rem] border-2 border-dashed border-[#B2C7C0] shadow-md max-w-md mx-auto lg:mx-0 text-left flex items-center gap-5 transform hover:scale-102 transition-transform duration-300">
              <div className="bg-[#B34B62] p-3.5 rounded-2xl text-white shadow-md">
                <Phone className="w-6.5 h-6.5 animate-bounce" />
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#3A5A4A] font-extrabold block">Ein Anruf genügt – Wir kümmern uns!</span>
                <a href="tel:01702657017" className="text-2xl font-black text-[#1C3B2B] hover:text-[#B34B62] transition-colors">0170 / 265 70 17</a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
              <a 
                href="#kontakt" 
                data-testid="hero-cta-button"
                onClick={triggerHeartClick}
                className="bg-[#B34B62] text-white hover:bg-[#9A3A4D] px-8 py-4.5 rounded-full text-base font-extrabold shadow-lg hover:shadow-xl transition-all w-full sm:w-auto text-center transform hover:-translate-y-0.5 relative overflow-hidden"
              >
                Kostenlose Beratung anfragen
              </a>
              <a 
                href="#services" 
                className="bg-transparent hover:bg-[#1C3B2B]/5 text-[#1C3B2B] border-2 border-[#1C3B2B] px-8 py-4.5 rounded-full text-base font-extrabold transition-all w-full sm:w-auto text-center"
              >
                Unsere Services ansehen
              </a>
            </div>
          </div>

          {/* Hero Image Showcase - Poppy Flyer Grandma */}
          <div className="lg:col-span-5 relative flex justify-center items-center min-h-[520px] sm:min-h-[560px]">
            {/* Framed grandma card */}
            <div className="relative z-10 w-[300px] sm:w-[360px] rounded-[2.5rem] overflow-hidden border-[6px] border-white shadow-[0_25px_60px_rgba(74,93,35,0.25)] rotate-1 animate-float-slow">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/ff726b9a-ad10-4587-8d0f-73307174a191/images/c12aeb96e858e0a8bfc554916f378f1969d15dae42e88cfb58abf901108310fc.png" 
                alt="HerzWerk Oma - Ich regel das mit HerzWerk" 
                className="w-full h-full object-cover"
              />
            </div>

            {/* Sticky Note: Ein Anruf genügt */}
            <div className="absolute top-0 right-2 sm:right-4 z-20 bg-[#F6EBC9] px-5 py-4 shadow-xl rotate-6 max-w-[150px] border border-[#E8DBAE]" style={{ borderRadius: '4px' }}>
              <p className="font-handwritten text-lg leading-tight text-[#4A5D23] font-bold text-center">
                Ein Anruf genügt – wir kümmern uns!
              </p>
              <Heart className="w-3.5 h-3.5 fill-[#C63B6E] text-[#C63B6E] mx-auto mt-1" />
            </div>

            {/* Pink Round Badge: Mit Herz, Verstand... */}
            <div className="absolute bottom-4 left-0 sm:left-2 z-20 w-32 h-32 sm:w-36 sm:h-36 rounded-full bg-[#C63B6E] text-white flex items-center justify-center shadow-2xl border-4 border-white animate-pulse-slow">
              <p className="font-handwritten text-base sm:text-lg leading-tight text-center font-bold px-4">
                Mit Herz, Verstand &amp; einem Lächeln für Sie da! <span className="text-xl">❤️</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Core Slogan Ribbon / Scroll Marquee */}
      <section className="bg-[#1C3B2B] text-white py-14 relative overflow-hidden border-y-4 border-[#B34B62]">
        <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#F4EFE6_1px,transparent_1px)] [background-size:20px_20px]" />
        
        {/* Hand drawn hearts in background */}
        <div className="absolute top-3 left-10 text-white/10 text-4xl rotate-12 font-sans select-none">❤️</div>
        <div className="absolute bottom-3 right-10 text-white/10 text-4xl -rotate-12 font-sans select-none">❤️</div>

        <div className="max-w-7xl mx-auto px-6 sm:px-12 text-center relative z-10 space-y-3">
          <p className="text-xs uppercase tracking-[0.3em] text-[#DDEBE6] font-black">Zuverlässig, herzlich & persönlich</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif italic font-medium leading-relaxed max-w-5xl mx-auto text-white">
            &ldquo;Zuhause ist, wo es am schönsten ist!&rdquo;
          </h2>
          <p className="font-handwritten text-xl sm:text-2xl text-[#DDEBE6] font-bold pt-2">
            – Ihr verlässlicher Alltagsbegleiter an Ihrer Seite
          </p>
        </div>
      </section>

      {/* 4. Services Section (Bento Grid) */}
      <section id="services" className="py-28 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-20 space-y-4">
            <span className="text-xs uppercase tracking-[0.25em] text-[#B34B62] font-black block">Unser Angebot</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#1C3B2B] font-medium leading-tight">
              Unsere Dienstleistungen im Überblick
            </h2>
            <p className="text-base text-[#3A5A4A] font-medium">
              Egal ob Sie Hilfe beim Wocheneinkauf benötigen, eine Begleitung zum Arzt wünschen, oder Unterstützung bei Reparaturen und Gartenarbeiten brauchen – wir sind mit Herz und Tatkraft für Sie zur Stelle.
            </p>
          </div>

          {/* Bento Grid (Tetris Mode as specified by Design guidelines) */}
          <div className="grid grid-cols-1 md:grid-cols-8 gap-8">
            {services.map((svc, idx) => {
              const IconComp = svc.icon;
              return (
                <motion.div 
                  key={idx}
                  data-testid={HOME.serviceCard}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: idx * 0.05 }}
                  className={`col-span-1 ${svc.colSpan} ${svc.bg} border-2 ${svc.border} rounded-[2.5rem] p-8 sm:p-10 shadow-[0_8px_30px_rgba(28,59,43,0.02)] hover:shadow-[0_20px_50px_rgba(28,59,43,0.06)] hover:-translate-y-1.5 hover:rotate-[0.5deg] transition-all duration-300 flex flex-col justify-between group`}
                >
                  <div className="space-y-6">
                    <div className="w-16 h-14 bg-white border border-[#E8E2D5] rounded-2xl flex items-center justify-center text-[#B34B62] transform group-hover:rotate-6 group-hover:scale-105 transition-all shadow-sm">
                      <IconComp className="w-8 h-8" />
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-2xl font-serif font-bold text-[#1C3B2B] group-hover:text-[#B34B62] transition-colors">
                          {svc.title}
                        </h3>
                        <Heart className="w-4.5 h-4.5 text-[#B34B62] fill-transparent group-hover:fill-[#B34B62] group-hover:text-[#B34B62] transition-all opacity-0 group-hover:opacity-100" />
                      </div>
                      <p className="text-[#3A5A4A] text-sm sm:text-base leading-relaxed font-medium">
                        {svc.description}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#E8E2D5] flex items-center gap-2 text-xs font-black text-[#B34B62] uppercase tracking-widest group-hover:translate-x-1.5 transition-transform">
                    <span>Mehr erfahren</span>
                    <ChevronRight className="w-4 h-4 text-[#B34B62] stroke-[3]" />
                  </div>
                </motion.div>
              );
            })}
          </div>

        </div>
      </section>

      {/* 5. Warum HerzWerk Section (Cute Post-it/Sticky paper design) */}
      <section id="warum-uns" className="py-24 bg-[#F4EFE6] relative overflow-hidden paper-texture">
        {/* Decorative corner tapes */}
        <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-[#1C3B2B]/10 to-transparent -z-10" />

        <div className="max-w-7xl mx-auto px-6 sm:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          
          {/* Left: Drill Grandma with flyer speech bubble */}
          <div className="lg:col-span-5 order-2 lg:order-1 relative flex justify-center items-center min-h-[480px]">
            {/* Framed drill grandma card */}
            <div className="relative z-10 w-[300px] sm:w-[350px] rounded-[2.5rem] overflow-hidden border-[6px] border-white shadow-[0_25px_60px_rgba(74,93,35,0.25)] -rotate-1 mt-16 animate-float-slow">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/ff726b9a-ad10-4587-8d0f-73307174a191/images/41840137e0cf533717f34aed65db6d8a8d7061f1fd3e17f7d7120e9f72d58e2e.png" 
                alt="HerzWerk Handwerker Oma" 
                className="w-full h-full object-cover"
              />
            </div>

            {/* Speech bubble from flyer */}
            <div className="absolute top-0 left-2 sm:left-6 z-20 bg-white px-6 py-5 rounded-[2rem] shadow-xl border-2 border-[#E8E2D5] max-w-[240px] rotate-[-3deg]">
              <div className="absolute bottom-[-12px] right-12 w-6 h-6 bg-white border-b-2 border-r-2 border-[#E8E2D5] rotate-45" />
              <p className="font-handwritten text-xl sm:text-2xl leading-tight text-[#4A5D23] font-bold">
                Ich kann zwar nicht alles... aber was ich kann, mach ich <span className="text-[#C63B6E] underline decoration-wavy">gern!</span>
              </p>
            </div>
          </div>

          {/* Right: Why us content & Sticky note list */}
          <div className="lg:col-span-7 order-1 lg:order-2 space-y-8">
            <div className="space-y-4">
              <span className="text-xs uppercase tracking-[0.25em] text-[#B34B62] font-black block">Warum HerzWerk?</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#1C3B2B] font-medium leading-tight">
                Zuhause liebevoll und zuverlässig umsorgt
              </h2>
              <p className="text-[#3A5A4A] text-base leading-relaxed font-medium">
                Unsere Alltagshelfer sind sorgfältig ausgewählt, absolut vertrauenswürdig und zeichnen sich durch ein hohes Maß an Empathie und Geduld aus. Bei uns stehen Sie als Mensch im Mittelpunkt.
              </p>
            </div>

            {/* Post-it Note layout from flyer */}
            <div className="bg-amber-50/80 p-8 sm:p-12 rounded-[3rem] border border-amber-200/80 shadow-[10px_10px_35px_rgba(28,59,43,0.04)] rotate-[-0.5deg] relative">
              <div className="absolute top-5 right-10 text-amber-300 text-5xl font-handwritten select-none opacity-40">❤️</div>
              
              <h3 className="text-2xl font-handwritten font-bold text-[#B34B62] mb-6 flex items-center gap-2">
                Unsere Kernwerte auf einen Blick:
              </h3>

              <ul className="space-y-5">
                {[
                  {
                    title: "Zuverlässig & vertrauensvoll",
                    desc: "Pünktlichkeit, Diskretion und Zuverlässigkeit sind für uns selbstverständlich."
                  },
                  {
                    title: "Persönlich & individuell",
                    desc: "Immer eine vertraute Bezugsperson, die genau Ihre Wünsche und Gewohnheiten kennt."
                  },
                  {
                    title: "Flexible Unterstützung nach Ihren Wünschen",
                    desc: "Keine starren Verträge. Wir unterstützen genau dann und so, wie Sie es brauchen."
                  },
                  {
                    title: "Mit Herz und einem offenen Ohr",
                    desc: "Zeit für ein gemütliches Gespräch, eine Tasse Kaffee und gemeinsames Lachen."
                  }
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-4 items-start">
                    <div className="w-6.5 h-6.5 rounded-full bg-[#B34B62] text-white flex items-center justify-center shrink-0 shadow-sm mt-0.5">
                      <Check className="w-4 h-4 stroke-[3]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1C3B2B] text-lg leading-snug">{item.title}</h4>
                      <p className="text-sm text-[#3A5A4A] leading-relaxed font-medium mt-1">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Contact Form Section */}
      <section id="kontakt" className="py-24 bg-[#F4EFE6] relative overflow-hidden">
        {/* Background Blobs */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#B34B62]/5 blur-3xl -z-10" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#DDEBE6]/50 blur-3xl -z-10" />

        <div className="max-w-7xl mx-auto px-6 sm:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          
          {/* Left: Contact details */}
          <div className="lg:col-span-5 space-y-10">
            <div className="space-y-4">
              <span className="text-xs uppercase tracking-[0.25em] text-[#B34B62] font-black block">Hier erreichen Sie uns</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#1C3B2B] font-medium leading-tight">
                Ich bin gerne persönlich für Sie da! ❤️
              </h2>
              <p className="text-[#3A5A4A] text-base leading-relaxed font-medium">
                Sprechen Sie uns an – für ein erstes, völlig unverbindliches Telefongespräch oder einen Besuch bei Ihnen Zuhause. Gemeinsam besprechen wir, wie wir Ihren Alltag erleichtern können.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-5 bg-[#FDFBF7] rounded-[2rem] border-2 border-[#E8E2D5] shadow-sm transform hover:scale-102 transition-transform duration-300">
                <div className="w-12 h-12 rounded-2xl bg-[#B34B62]/10 text-[#B34B62] flex items-center justify-center shrink-0">
                  <Phone className="w-6 h-6 stroke-[2.5]" />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-[#3A5A4A] font-extrabold block mb-1">Direkter Draht</span>
                  <a href="tel:01702657017" className="text-xl font-black text-[#1C3B2B] hover:text-[#B34B62] transition-colors">0170 / 265 70 17</a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-[#FDFBF7] rounded-[2rem] border-2 border-[#E8E2D5] shadow-sm transform hover:scale-102 transition-transform duration-300">
                <div className="w-12 h-12 rounded-2xl bg-[#B34B62]/10 text-[#B34B62] flex items-center justify-center shrink-0">
                  <Mail className="w-6 h-6 stroke-[2.5]" />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-[#3A5A4A] font-extrabold block mb-1">E-Mail-Adresse</span>
                  <a href="mailto:info@herzwerk-bavaria.de" className="text-xl font-black text-[#1C3B2B] hover:text-[#B34B62] transition-colors">info@herzwerk-bavaria.de</a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-[#FDFBF7] rounded-[2rem] border-2 border-[#E8E2D5] shadow-sm transform hover:scale-102 transition-transform duration-300">
                <div className="w-12 h-12 rounded-2xl bg-[#B34B62]/10 text-[#B34B62] flex items-center justify-center shrink-0">
                  <MapPin className="w-6 h-6 stroke-[2.5]" />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-[#3A5A4A] font-extrabold block mb-1">Einsatzort</span>
                  <p className="text-xl font-black text-[#1C3B2B]">Ihr Einsatzgebiet in Bayern</p>
                </div>
              </div>
            </div>

            {/* Test Dashboard Trigger */}
            <div className="pt-6 border-t border-[#E8E2D5]">
              <button 
                data-testid={HOME.adminToggle}
                onClick={() => setIsAdminOpen(!isAdminOpen)}
                className="inline-flex items-center gap-2 text-xs font-black text-[#3A5A4A] hover:text-[#B34B62] transition-colors uppercase tracking-widest"
              >
                {isAdminOpen ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                {isAdminOpen ? "Admin-Bereich schließen" : "Kundenanfragen einsehen (Demo)"}
              </button>
            </div>
          </div>

          {/* Right: Contact form card */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-[3rem] border-2 border-[#E8E2D5] p-8 sm:p-12 shadow-[0_15px_45px_rgba(28,59,43,0.06)] relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#DDEBE6]/40 rounded-full -z-10" />
              
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-3xl font-serif font-bold text-[#1C3B2B]">Schreiben Sie uns</h3>
                  <p className="text-sm text-[#3A5A4A] font-medium">Füllen Sie einfach das Formular aus, wir melden uns umgehend bei Ihnen.</p>
                </div>
                
                {submitSuccess && (
                  <div 
                    data-testid={HOME.contactFormSuccessAlert}
                    className="p-4.5 bg-[#DDEBE6] text-[#1C3B2B] rounded-2xl border-2 border-[#B2C7C0] text-sm font-semibold shadow-sm flex items-center gap-2"
                  >
                    <Sparkles className="w-5 h-5 text-[#B34B62] fill-[#B34B62] shrink-0" />
                    <span>{submitSuccess}</span>
                  </div>
                )}

                {submitError && (
                  <div 
                    data-testid={HOME.contactFormErrorAlert}
                    className="p-4.5 bg-red-50 text-red-800 rounded-2xl border-2 border-red-200 text-sm font-semibold"
                  >
                    {submitError}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-[#3A5A4A] font-black">Ihr Name</label>
                    <input 
                      type="text" 
                      required
                      data-testid={HOME.contactFormName}
                      placeholder="z.B. Maria Huber"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      className="w-full bg-[#FDFBF7] border-2 border-[#E8E2D5] rounded-xl px-4 py-3.5 text-sm text-[#1C3B2B] focus:outline-none focus:ring-2 focus:ring-[#B34B62] focus:border-transparent transition-all font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-[#3A5A4A] font-black">E-Mail-Adresse</label>
                    <input 
                      type="email" 
                      required
                      data-testid={HOME.contactFormEmail}
                      placeholder="z.B. maria@beispiel.de"
                      value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)}
                      className="w-full bg-[#FDFBF7] border-2 border-[#E8E2D5] rounded-xl px-4 py-3.5 text-sm text-[#1C3B2B] focus:outline-none focus:ring-2 focus:ring-[#B34B62] focus:border-transparent transition-all font-medium"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-[#3A5A4A] font-black">Telefonnummer</label>
                    <input 
                      type="tel" 
                      required
                      data-testid={HOME.contactFormPhone}
                      placeholder="z.B. 0172 / 987 65 43"
                      value={formPhone}
                      onChange={(e) => setFormPhone(e.target.value)}
                      className="w-full bg-[#FDFBF7] border-2 border-[#E8E2D5] rounded-xl px-4 py-3.5 text-sm text-[#1C3B2B] focus:outline-none focus:ring-2 focus:ring-[#B34B62] focus:border-transparent transition-all font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-[#3A5A4A] font-black">Gewünschter Service</label>
                    <select 
                      required
                      data-testid={HOME.contactFormService}
                      value={formService}
                      onChange={(e) => setFormService(e.target.value)}
                      className="w-full bg-[#FDFBF7] border-2 border-[#E8E2D5] rounded-xl px-4 py-3.5 text-sm text-[#1C3B2B] focus:outline-none focus:ring-2 focus:ring-[#B34B62] focus:border-transparent transition-all font-semibold"
                    >
                      <option value="" disabled>Bitte auswählen...</option>
                      <option value="Einkaufsservice">Einkaufsservice</option>
                      <option value="Arztbegleitung & Fahrdienst">Arztbegleitung & Fahrdienst</option>
                      <option value="Reisebegleitung">Reisebegleitung</option>
                      <option value="Gartenarbeiten">Gartenarbeiten</option>
                      <option value="Tierbetreuung">Tierbetreuung</option>
                      <option value="Handwerkliche Arbeiten">Handwerkliche Arbeiten</option>
                      <option value="Weitere Unterstützung">Weitere Unterstützung</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-widest text-[#3A5A4A] font-black">Ihre Nachricht</label>
                  <textarea 
                    rows="4"
                    required
                    data-testid={HOME.contactFormMessage}
                    placeholder="Erzählen Sie uns kurz, worum es geht..."
                    value={formMessage}
                    onChange={(e) => setFormMessage(e.target.value)}
                    className="w-full bg-[#FDFBF7] border-2 border-[#E8E2D5] rounded-xl px-4 py-3.5 text-sm text-[#1C3B2B] focus:outline-none focus:ring-2 focus:ring-[#B34B62] focus:border-transparent transition-all resize-none font-medium"
                  />
                </div>

                {/* DSGVO Datenschutz-Hinweis & Pflicht-Checkbox */}
                <div className="flex items-start gap-3 bg-[#FDFBF7] border-2 border-[#E8E2D5] rounded-2xl p-4">
                  <input 
                    type="checkbox"
                    id="privacy-consent"
                    required
                    data-testid="contact-form-privacy"
                    checked={formPrivacy}
                    onChange={(e) => setFormPrivacy(e.target.checked)}
                    className="mt-1 h-5 w-5 shrink-0 rounded border-2 border-[#B34B62] text-[#B34B62] focus:ring-2 focus:ring-[#B34B62] cursor-pointer accent-[#B34B62]"
                  />
                  <label htmlFor="privacy-consent" className="text-xs text-[#3A5A4A] leading-relaxed font-medium cursor-pointer">
                    Ich habe die{" "}
                    <button 
                      type="button"
                      data-testid="privacy-link-in-form"
                      onClick={() => setActiveModal('datenschutz')}
                      className="text-[#B34B62] font-bold underline hover:text-[#9A3A4D] transition-colors"
                    >
                      Datenschutzerklärung
                    </button>{" "}
                    gelesen und stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert und verarbeitet werden. Sie können Ihre Einwilligung jederzeit für die Zukunft per E-Mail widerrufen.<span className="text-[#B34B62] font-bold"> *</span>
                  </label>
                </div>

                <button 
                  type="submit"
                  data-testid={HOME.contactFormSubmit}
                  disabled={isSubmitting}
                  onClick={triggerHeartClick}
                  className="w-full bg-[#B34B62] hover:bg-[#9A3A4D] disabled:bg-[#B34B62]/60 text-white font-extrabold py-4 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 transform hover:-translate-y-0.5 relative overflow-hidden"
                >
                  {isSubmitting ? "Wird gesendet..." : "Anfrage absenden"}
                  {floatingHearts.map((heart) => (
                    <FloatingHeart 
                      key={heart.id} 
                      x={heart.x} 
                      y={heart.y} 
                      id={heart.id} 
                      onComplete={removeHeart} 
                    />
                  ))}
                </button>
              </form>
            </div>
          </div>

        </div>
      </section>

      {/* 7. Collapsible Admin Submission Dashboard */}
      <AnimatePresence>
        {isAdminOpen && (
          <motion.section 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-[#E8E2D5] bg-[#FDFBF7] py-12"
          >
            <div className="max-w-7xl mx-auto px-6 sm:px-12 space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-[#E8E2D5] pb-6">
                <div>
                  <h3 className="text-2xl font-serif font-bold text-[#1C3B2B]">Admin Dashboard</h3>
                  <p className="text-xs text-[#3A5A4A] uppercase tracking-widest mt-1">Sicherer Zugriff auf Kundenanfragen</p>
                </div>
                {isAuthenticated && (
                  <button 
                    onClick={handleAdminLogout}
                    className="bg-[#1C3B2B] text-white hover:bg-[#1C3B2B]/85 px-4 py-2 rounded-full text-xs font-semibold"
                  >
                    Ausloggen
                  </button>
                )}
              </div>

              {!isAuthenticated ? (
                /* Admin PIN Auth Form */
                <form onSubmit={handleAdminAuth} className="max-w-sm space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-[#3A5A4A] font-bold block">Geben Sie den Admin-PIN ein (PIN: 1234)</label>
                    <div className="flex gap-2">
                      <input 
                        type="password" 
                        required
                        data-testid={HOME.adminPinInput}
                        placeholder="PIN eingeben..."
                        value={adminPin}
                        onChange={(e) => setAdminPin(e.target.value)}
                        className="bg-white border border-[#E8E2D5] rounded-xl px-4 py-2 text-sm text-[#1C3B2B] focus:outline-none focus:ring-2 focus:ring-[#B34B62] transition-all flex-grow"
                      />
                      <button 
                        type="submit"
                        className="bg-[#B34B62] text-white hover:bg-[#9A3A4D] px-6 py-2 rounded-xl text-sm font-semibold"
                      >
                        Anmelden
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                /* Contact Submissions list */
                <div className="space-y-6">
                  {/* Search and count */}
                  <div className="flex flex-col sm:flex-row justify-between gap-4 items-center">
                    <div className="relative w-full sm:max-w-xs">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#3A5A4A]/60" />
                      <input 
                        type="text" 
                        placeholder="Anfragen durchsuchen..."
                        value={msgSearch}
                        onChange={(e) => setMsgSearch(e.target.value)}
                        className="pl-9 pr-4 py-2 w-full bg-white border border-[#E8E2D5] rounded-xl text-xs text-[#1C3B2B] focus:outline-none focus:ring-1 focus:ring-[#B34B62]"
                      />
                    </div>
                    <span className="text-xs font-semibold text-[#3A5A4A] bg-[#DDEBE6] px-3 py-1.5 rounded-full">
                      Insgesamt: {filteredMessages.length} Anfrage(n)
                    </span>
                  </div>

                  {msgLoading ? (
                    <div className="text-center py-8 text-[#3A5A4A] text-sm font-medium">Lade Anfragen...</div>
                  ) : filteredMessages.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-3xl border border-[#E8E2D5] text-[#3A5A4A] text-sm">
                      Keine Anfragen gefunden.
                    </div>
                  ) : (
                    /* Table */
                    <div className="overflow-x-auto rounded-3xl border border-[#E8E2D5] shadow-sm bg-white">
                      <table className="w-full text-left text-sm border-collapse">
                        <thead>
                          <tr className="bg-[#F4EFE6] border-b border-[#E8E2D5] text-xs uppercase tracking-widest text-[#3A5A4A] font-extrabold">
                            <th className="p-4">Kunde</th>
                            <th className="p-4">Dienstleistung</th>
                            <th className="p-4">Nachricht</th>
                            <th className="p-4">Datum</th>
                            <th className="p-4 text-right">Aktion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredMessages.map((msg) => (
                            <tr 
                              key={msg.id} 
                              data-testid={HOME.adminMessageRow}
                              className="border-b border-[#E8E2D5] hover:bg-[#FDFBF7]/50 transition-colors"
                            >
                              <td className="p-4">
                                <div className="font-bold text-[#1C3B2B]">{msg.name}</div>
                                <div className="text-xs text-[#3A5A4A] mt-1">{msg.email}</div>
                                <div className="text-xs text-[#3A5A4A]">{msg.phone}</div>
                              </td>
                              <td className="p-4 font-semibold">
                                <span className="px-2.5 py-1 rounded-full bg-[#DDEBE6] text-[#1C3B2B] text-xs font-semibold">
                                  {msg.service}
                                </span>
                              </td>
                              <td className="p-4 text-xs text-[#3A5A4A] max-w-xs truncate" title={msg.message}>
                                {msg.message}
                              </td>
                              <td className="p-4 text-xs text-[#3A5A4A]">
                                {new Date(msg.timestamp).toLocaleString("de-DE")}
                              </td>
                              <td className="p-4 text-right">
                                <button 
                                  data-testid={HOME.adminDeleteButton}
                                  onClick={() => handleDeleteMessage(msg.id)}
                                  className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-all"
                                  aria-label="Löschen"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* 8. Legal Modals (Impressum & Datenschutz) */}
      <AnimatePresence>
        {activeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveModal(null)}
              className="absolute inset-0 bg-[#1C3B2B]/60 backdrop-blur-sm"
            />

            {/* Modal Body */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#FDFBF7] rounded-[3rem] border-2 border-[#E8E2D5] shadow-2xl relative w-full max-w-3xl max-h-[85vh] overflow-y-auto z-10 p-8 sm:p-12"
              data-testid={activeModal === 'impressum' ? HOME.impressumDialog : HOME.datenschutzDialog}
            >
              {/* Close Button */}
              <button 
                data-testid={HOME.closeDialog}
                onClick={() => setActiveModal(null)}
                className="absolute top-6 right-6 p-2 rounded-full hover:bg-[#F4EFE6] text-[#1C3B2B] transition-colors"
                aria-label="Schließen"
              >
                <X className="w-6.5 h-6.5" />
              </button>

              {activeModal === 'impressum' ? (
                /* Impressum content */
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 text-[#B34B62]">
                    <Scale className="w-6.5 h-6.5 stroke-[2]" />
                    <h3 className="text-3xl font-serif font-bold text-[#1C3B2B]">Impressum</h3>
                  </div>
                  
                  <div className="space-y-6 text-sm text-[#3A5A4A] leading-relaxed font-medium">
                    <p className="font-extrabold text-[#1C3B2B] text-base">HerzWerk – Alltagshilfe & Hausservice</p>

                    <div>
                      <p className="font-bold text-[#1C3B2B] mb-1">Angaben gemäß § 5 TMG:</p>
                      <p>Claudia Feichtinger<br />Schürzingerstraße 5<br />94051 Hauzenberg<br />Bayern, Deutschland</p>
                    </div>

                    <div>
                      <p className="font-bold text-[#1C3B2B] mb-1">Kontakt:</p>
                      <p>Telefon: 0170 / 265 70 17<br />E-Mail: info@herzwerk-bavaria.de</p>
                    </div>

                    <div>
                      <p className="font-bold text-[#1C3B2B] mb-1">Umsatzsteuer:</p>
                      <p>Gemäß § 19 UStG (Kleinunternehmerregelung) wird keine Umsatzsteuer berechnet und ausgewiesen.</p>
                    </div>

                    <div className="border-t border-[#E8E2D5] pt-4 text-xs text-[#3A5A4A] font-normal">
                      <p>Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV: Claudia Feichtinger (Anschrift wie oben)</p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Datenschutz content */
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 text-[#B34B62]">
                    <ShieldAlert className="w-6.5 h-6.5 stroke-[2]" />
                    <h3 className="text-3xl font-serif font-bold text-[#1C3B2B]">Datenschutzerklärung</h3>
                  </div>
                  
                  <div className="space-y-6 text-sm text-[#3A5A4A] leading-relaxed font-medium">
                    <div>
                      <h4 className="font-extrabold text-[#1C3B2B] text-lg mb-2">1. Datenschutz auf einen Blick</h4>
                      <p>Der Schutz Ihrer persönlichen Daten ist uns ein großes Anliegen. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften (DSGVO) sowie dieser Datenschutzerklärung.</p>
                    </div>

                    <div>
                      <h4 className="font-extrabold text-[#1C3B2B] text-lg mb-2">2. Verantwortliche Stelle</h4>
                      <p>Verantwortlich für die Datenverarbeitung auf dieser Website ist:</p>
                      <p className="mt-1">Claudia Feichtinger<br />Schürzingerstraße 5<br />94051 Hauzenberg<br />Telefon: 0170 / 265 70 17<br />E-Mail: info@herzwerk-bavaria.de</p>
                    </div>

                    <div>
                      <h4 className="font-extrabold text-[#1C3B2B] text-lg mb-2">3. Datenerfassung auf unserer Website</h4>
                      <p className="font-bold text-[#1C3B2B] mb-1">Kontaktformular:</p>
                      <p>Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten (Name, E-Mail-Adresse, Telefonnummer, gewünschte Dienstleistung) zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Die Verarbeitung erfolgt auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.</p>
                    </div>

                    <div>
                      <h4 className="font-extrabold text-[#1C3B2B] text-lg mb-2">4. Ihre Rechte</h4>
                      <p>Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung, Sperrung oder Löschung dieser Daten zu verlangen sowie eine erteilte Einwilligung jederzeit zu widerrufen. Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit an die oben genannte verantwortliche Stelle wenden.</p>
                    </div>

                    <div>
                      <h4 className="font-extrabold text-[#1C3B2B] text-lg mb-2">5. SSL- bzw. TLS-Verschlüsselung</h4>
                      <p>Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie zum Beispiel Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://“ auf „https://“ wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.</p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* 9. Footer Section */}
      <footer className="bg-[#1C3B2B] text-white pt-20 pb-8 border-t-4 border-[#B34B62]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 border-b border-[#3A5A4A]/20 pb-12">
          
          {/* Col 1: Brand details */}
          <div className="md:col-span-4 space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#B34B62] rounded-xl flex items-center justify-center text-white">
                <Heart className="w-5 h-5 fill-white text-[#B34B62]" />
              </div>
              <span className="text-2xl font-bold tracking-tight text-white font-serif">HerzWerk</span>
            </div>
            
            <p className="text-[#DDEBE6] text-sm leading-relaxed">
              Professionelle Alltagshilfe und liebevoller Hausservice für Senioren in Bayern. Wir packen tatkräftig an, damit Sie Ihr Leben entspannt genießen können.
            </p>

            <div className="flex items-center gap-1.5 text-xs text-[#DDEBE6]">
              <span>Mit Liebe fürs Detail gestaltet</span>
              <Heart className="w-3.5 h-3.5 fill-[#B34B62] text-[#B34B62]" />
            </div>
          </div>

          {/* Col 2: Services Quick Links */}
          <div className="md:col-span-4 space-y-4">
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#DDEBE6] font-extrabold">Dienstleistungen</h4>
            <ul className="space-y-2.5 text-sm text-[#DDEBE6]/80 font-medium">
              <li><a href="#services" className="hover:text-white transition-colors flex items-center gap-1.5"><ChevronRight className="w-3.5 h-3.5 text-[#B34B62]" /> Einkaufsservice</a></li>
              <li><a href="#services" className="hover:text-white transition-colors flex items-center gap-1.5"><ChevronRight className="w-3.5 h-3.5 text-[#B34B62]" /> Arztbegleitung & Fahrdienst</a></li>
              <li><a href="#services" className="hover:text-white transition-colors flex items-center gap-1.5"><ChevronRight className="w-3.5 h-3.5 text-[#B34B62]" /> Reisebegleitung</a></li>
              <li><a href="#services" className="hover:text-white transition-colors flex items-center gap-1.5"><ChevronRight className="w-3.5 h-3.5 text-[#B34B62]" /> Gartenarbeiten</a></li>
              <li><a href="#services" className="hover:text-white transition-colors flex items-center gap-1.5"><ChevronRight className="w-3.5 h-3.5 text-[#B34B62]" /> Tierbetreuung</a></li>
              <li><a href="#services" className="hover:text-white transition-colors flex items-center gap-1.5"><ChevronRight className="w-3.5 h-3.5 text-[#B34B62]" /> Handwerkliche Arbeiten</a></li>
            </ul>
          </div>

          {/* Col 3: Contact & Bavarian Area */}
          <div className="md:col-span-4 space-y-4">
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#DDEBE6] font-extrabold">Unser Einsatzgebiet</h4>
            <div className="space-y-2.5 text-sm text-[#DDEBE6]/80 font-medium">
              <p className="font-extrabold text-white text-base">Freistaat Bayern 🥨</p>
              <p>Wir sind im gesamten bayerischen Raum für Sie einsatzbereit und sorgen für ein sorgenfreies Leben.</p>
              <p className="pt-2 font-bold text-white">Anrufen & Beraten lassen:</p>
              <p><a href="tel:01702657017" className="text-[#B34B62] hover:text-[#B34B62]/80 text-lg font-black transition-colors">0170 / 265 70 17</a></p>
            </div>
          </div>

        </div>

        {/* Footer legal & sub-navigation */}
        <div className="max-w-7xl mx-auto px-6 sm:px-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-[#DDEBE6]/60">
          <p>&copy; {new Date().getFullYear()} HerzWerk GmbH. Alle Rechte vorbehalten. Weil Ihr Zuhause unbezahlbar ist.</p>
          <div className="flex gap-6">
            <button 
              data-testid={HOME.impressumTrigger}
              onClick={() => setActiveModal('impressum')}
              className="hover:text-white transition-colors uppercase tracking-widest font-extrabold text-[10px]"
            >
              Impressum
            </button>
            <button 
              data-testid={HOME.datenschutzTrigger}
              onClick={() => setActiveModal('datenschutz')}
              className="hover:text-white transition-colors uppercase tracking-widest font-extrabold text-[10px]"
            >
              Datenschutz
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
